import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { updateMyPreferences, fetchPublishedLocales, fetchResolvedLocale } from "@/lib/apiClient";

import en from "@/i18n/locales/en.json";
import ta from "@/i18n/locales/ta.json";
import ur from "@/i18n/locales/ur.json";
import ms from "@/i18n/locales/ms.json";
import sw from "@/i18n/locales/sw.json";

/**
 * i18n — bare React context, no extra dependency (react-i18next was
 * considered and skipped: this already covers keyed lookup, interpolation,
 * pluralization, and RTL; a migration would touch 30+ call sites for no
 * functional gain over extending this).
 *
 * Two layers of translation data:
 *  1. Static bundle (this file's DICTIONARIES) — the 5 locales translated
 *     directly in-repo (en/ta/ur/ms/sw). Used for instant first paint before
 *     the network call below resolves, and as the offline fallback.
 *  2. Backend-resolved dict (GET /api/i18n/{locale}) — merges the DB-backed
 *     admin-override layer on top of the machine/professional base layer on
 *     top of English, per locale. This is what makes an admin's edit in the
 *     Translations panel show up without a redeploy, and is how the other
 *     15 launch languages (draft until reviewed) get served at all — they
 *     have no static bundle entry.
 * The remote dict, once loaded for the active locale, takes over from the
 * static one; `t()` doesn't care which supplied the value.
 *
 * Persists to `localStorage.qf:lang` AND, when signed in, syncs to the
 * `users.language_preference` document so the choice follows users across
 * devices. RTL locales (currently Urdu; Pashto/Kashmiri once published) flip
 * the global `dir="rtl"` on <html>; the rest remain LTR.
 *
 * Default lang detection: navigator.language matched against supported list,
 * fallback to English. So a user on `ur-PK` lands in Urdu automatically.
 */
export const LANGUAGES = [
  { code: "en", label: "English",        native: "English",   dir: "ltr" },
  { code: "ta", label: "Tamil",          native: "தமிழ்",       dir: "ltr" },
  { code: "ur", label: "Urdu",           native: "اردو",        dir: "rtl" },
  { code: "ms", label: "Bahasa Malayu",  native: "Bahasa Melayu", dir: "ltr" },
  { code: "sw", label: "Swahili",        native: "Kiswahili",  dir: "ltr" },
];

const DICTIONARIES = { en, ta, ur, ms, sw };
const STORAGE_KEY = "qf:lang";
const CHOSEN_KEY = "qf:lang-chosen";

function detectDefault(knownCodes) {
  if (typeof navigator === "undefined") return "en";
  const tags = [].concat(navigator.languages || []).concat([navigator.language || ""]).filter(Boolean);
  for (const tag of tags) {
    const prefix = String(tag).toLowerCase().split(/[-_]/)[0];
    if (knownCodes.has(prefix)) return prefix;
  }
  return "en";
}

function interpolate(template, vars) {
  if (!template || typeof template !== "string" || !vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

// Plural-rule cache — one Intl.PluralRules instance per locale (construction
// isn't free, and t() runs on every render).
const pluralRulesCache = {};
function pluralCategory(locale, n) {
  if (!pluralRulesCache[locale]) {
    try { pluralRulesCache[locale] = new Intl.PluralRules(locale); }
    catch { pluralRulesCache[locale] = new Intl.PluralRules("en"); }
  }
  return pluralRulesCache[locale].select(n);
}

const I18nCtx = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
  languages: LANGUAGES,
});

export function I18nProvider({ children }) {
  const { user, setUser } = useAuth();

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // Published-locale list — starts as the static bundle (5 locales, correct
  // immediately, no flash) and is replaced once /api/i18n/locales resolves,
  // so newly-published locales (the other 15, as admins review and publish
  // them) show up in the switcher without a frontend redeploy.
  const [languages, setLanguages] = useState(LANGUAGES);
  useEffect(() => {
    let cancelled = false;
    fetchPublishedLocales()
      .then((data) => {
        if (cancelled || !data?.locales?.length) return;
        setLanguages(data.locales.map((l) => ({ code: l.code, label: l.label, native: l.native, dir: l.dir })));
      })
      .catch(() => { /* offline — static LANGUAGES list still correct */ });
    return () => { cancelled = true; };
  }, []);
  const knownCodes = useMemo(() => new Set(languages.map((l) => l.code)), [languages]);

  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    // Admin "Preview live" link (AdminTranslationEditor) — forces a locale
    // via URL, without touching the stored preference or backend sync, so
    // an admin previewing Tamil doesn't change their own account's language.
    const previewLang = new URLSearchParams(window.location.search).get("preview_lang");
    if (previewLang) return previewLang;
    // Trust a stored explicit choice even for a locale outside the static
    // bundle (one of the 15 launch languages with no in-repo translation
    // file) — t() already falls back to English until the remote-resolved
    // dict fetch below lands, then swaps over; gating this on DICTIONARIES
    // would silently discard the user's choice back to browser-detected.
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || detectDefault(new Set(Object.keys(DICTIONARIES)));
  });

  // Remote-resolved dict for the active locale (override -> base -> English,
  // computed server-side). `remote.lang` guards against a race where the
  // user switches languages again before the previous fetch lands.
  const [remote, setRemote] = useState(null); // { lang, dict } | null
  useEffect(() => {
    let cancelled = false;
    fetchResolvedLocale(lang)
      .then((dict) => { if (!cancelled) setRemote({ lang, dict }); })
      .catch(() => { /* offline / backend down — static bundle (if any) still serves t() */ });
    return () => { cancelled = true; };
  }, [lang]);

  // Reflect lang + dir onto <html> so global CSS / browser hooks pick it up.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const entry = languages.find((l) => l.code === lang) || languages[0];
    document.documentElement.setAttribute("lang", entry.code);
    document.documentElement.setAttribute("dir", entry.dir);
  }, [lang, languages]);

  // Hydrate from backend when user resolves (cross-device sync, same pattern
  // as ScriptSettingsContext).
  const hydratedFor = useRef(null);
  const pendingSyncRef = useRef(null);
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("preview_lang");
  useEffect(() => {
    if (isPreview) return; // admin preview link — never let the account's own language_preference clobber it
    if (!user) { hydratedFor.current = null; return; }
    if (pendingSyncRef.current && pendingSyncRef.current !== user.language_preference) {
      const v = pendingSyncRef.current;
      pendingSyncRef.current = null;
      updateMyPreferences({ language_preference: v })
        .then((u) => setUser && setUser(u))
        .catch(() => { /* offline — local fine */ });
      return;
    }
    if (hydratedFor.current === user.user_id) return;
    hydratedFor.current = user.user_id;
    // No knownCodes gate here deliberately: `language_preference` was only
    // ever written via setLang() below (already validated at that point),
    // and the backend's resolver falls back to an English floor for any
    // locale code regardless — gating on `languages` (which starts as the
    // static 5-locale list and is replaced async) would race a freshly-
    // published locale and, since `hydratedFor` short-circuits every run
    // after the first, silently never retry.
    const backendLang = user.language_preference;
    if (backendLang && backendLang !== lang) {
      setLangState(backendLang);
      try {
        localStorage.setItem(STORAGE_KEY, backendLang);
        localStorage.setItem(CHOSEN_KEY, "1");
      } catch (_e) { /* harmless */ }
    }
  }, [user, lang, setUser, isPreview]);

  const setLang = useCallback(async (next, { explicit = true } = {}) => {
    if (!knownCodes.has(next)) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      if (explicit) localStorage.setItem(CHOSEN_KEY, "1");
    } catch (_e) { /* harmless */ }
    if (!explicit) return;
    const latest = userRef.current;
    if (!latest?.user_id) {
      pendingSyncRef.current = next;
      return;
    }
    try {
      const updated = await updateMyPreferences({ language_preference: next });
      if (updated && setUser) setUser(updated);
    } catch (_e) { /* offline — local UX still good */ }
  }, [setUser, knownCodes]);

  const t = useMemo(() => {
    // Three-layer resolution:
    //  1. Remote dict (backend-resolved: admin overrides → machine base → en)
    //     — wins once the async fetch resolves; before that, layer 2 is used.
    //  2. Static locale bundle (ta.json / ur.json / …) — catches any key that
    //     exists in the shipped JSON but hasn't yet been seeded into i18n_base
    //     (e.g. a freshly added key that admin hasn't generated/reviewed yet).
    //  3. Static English bundle — final fallback so nothing ever shows a raw key.
    const dict = (remote && remote.lang === lang) ? remote.dict : (DICTIONARIES[lang] || DICTIONARIES.en);
    const staticLocale = DICTIONARIES[lang] || DICTIONARIES.en;
    const staticEn = DICTIONARIES.en;
    return (key, vars) => {
      // Pluralization: when vars.count is a number, resolve `${key}_<category>`
      // (Intl.PluralRules — zero/one/two/few/many/other, correct per-locale
      // rules rather than a hand-rolled singular/plural ternary) before
      // falling back to the bare key. `${key}_other` is the required
      // catch-all every plural key set must define.
      if (vars && typeof vars.count === "number") {
        const category = pluralCategory(lang, vars.count);
        const pluralKey = `${key}_${category}`;
        const otherKey = `${key}_other`;
        const v = dict[pluralKey] != null ? dict[pluralKey]
          : dict[otherKey] != null ? dict[otherKey]
          : staticLocale[pluralKey] != null ? staticLocale[pluralKey]
          : staticLocale[otherKey] != null ? staticLocale[otherKey]
          : staticEn[pluralKey] != null ? staticEn[pluralKey]
          : staticEn[otherKey] != null ? staticEn[otherKey]
          : key;
        return interpolate(v, vars);
      }
      const v = dict[key] != null ? dict[key]
        : staticLocale[key] != null ? staticLocale[key]
        : staticEn[key] != null ? staticEn[key]
        : key;
      return interpolate(v, vars);
    };
  }, [lang, remote]);

  const dir = (languages.find((l) => l.code === lang) || languages[0]).dir;

  return (
    <I18nCtx.Provider value={{ lang, setLang, t, dir, languages }}>
      {children}
    </I18nCtx.Provider>
  );
}

export const useI18n = () => useContext(I18nCtx);
